import { gql, useMutation, useQuery } from "@apollo/client";
import moment from "moment";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { ModalContext } from "src/context";
import { errorToast, successToast } from "src/utils/toast";
import styled from "styled-components";
import { theme } from "../../utils/theme";
import { AppText, FlexDiv } from "../UI";
import { PhoenixAppButton, PhoenixRadio } from "../UI/Phoenix";
import { Modal } from "./Modal";

interface DisappearingDivProps {
  blinds: boolean;
  close: () => void;
}

const FETCH_DISPOSITION_OPTIONS = gql`
  query fetchDispositionOptions($leadId: String!, $associatedAction: String, $person_spoke_to: CALLRESULT) {
    fetchDispositionOptions(lead_id: $leadId, associated_action: $associatedAction, person_spoke_to: $person_spoke_to) {
      id
      label
    }
  }
`;

const EDIT_CALENDAR_EVENT = gql`
  mutation editCalendarEvent(
    $id: String!
    $event_start_time: DateTime!
    $event_end_time: DateTime
    $event_title: String!
    $event_location: String
    $event_description: String!
    $joinMe_id: String
    $guest_list: [String]
    $disposition_id: String
    $meeting_type: MeetingType
    $event_timezone: String
  ) {
    editCalendarEvent(
      id: $id
      event_start_time: $event_start_time
      event_end_time: $event_end_time
      event_title: $event_title
      event_location: $event_location
      event_description: $event_description
      joinMe_id: $joinMe_id
      guest_list: $guest_list
      disposition_id: $disposition_id
      meeting_type: $meeting_type
      event_timezone: $event_timezone
    ) {
      id
    }
  }
`;

const MoveEventModal: React.FC<DisappearingDivProps> = ({ blinds, close }) => {
  const { moveEventData } = useContext(ModalContext);

  const [selectedDisposition, setSelectedDisposition] = useState<string | null>(null);

  const { data: dataDMOptions, loading: loadingDMOptions } = useQuery(FETCH_DISPOSITION_OPTIONS, {
    fetchPolicy: "cache-and-network",
    variables: {
      leadId: moveEventData?.leadId,
      associatedAction: moveEventData?.type === "Demo" ? "Reschedule Demo" : "Schedule Callback",
      person_spoke_to: "DM",
    },
    onCompleted: ({ fetchDispositionOptions }) => {
      // default to "Demo Rescheduled w/ DM" selection
      if (fetchDispositionOptions.find((o: any) => o.label === "Demo Rescheduled w/ DM")) {
        setSelectedDisposition(fetchDispositionOptions.find((o: any) => o.label === "Demo Rescheduled w/ DM").id);
      }
    },
  });
  const { data: dataNDMOptions, loading: loadingNDMOptions } = useQuery(FETCH_DISPOSITION_OPTIONS, {
    fetchPolicy: "cache-and-network",
    variables: {
      leadId: moveEventData?.leadId,
      associatedAction: moveEventData?.type === "Demo" ? "Reschedule Demo" : "ScheduleCallback",
      person_spoke_to: "NDM",
    },
  });

  const [editCalendarEvent, { loading: loadingEdit }] = useMutation(EDIT_CALENDAR_EVENT, {
    async onCompleted() {
      successToast("Event updated!");
      close();
    },
    onError({ message }) {
      errorToast(message);
      console.log("Error in editCalendarEvent: ", message);
    },
    refetchQueries: ["fetchUpcoming"],
  });

  const loadingDispositionOptions = useMemo(
    () => loadingDMOptions || loadingNDMOptions,
    [loadingDMOptions, loadingNDMOptions],
  );

  const formattedDispositionOptions = useMemo(() => {
    const dmOptions = dataDMOptions?.fetchDispositionOptions || [];
    const ndmOptions = dataNDMOptions?.fetchDispositionOptions || [];

    if (moveEventData?.type === "Demo") {
      return [
        {
          label: "Demo Rescheduled With DM",
          value: dmOptions.find((option: any) => option.label === "Demo Rescheduled w/ DM")?.id,
        },
        {
          label: "Demo Rescheduled With NDM",
          value: ndmOptions.find((option: any) => option.label === "Demo Rescheduled w/ NDM")?.id,
        },
        { label: "Partial Demo", value: dmOptions.find((option: any) => option.label === "Partial Demo")?.id },
        { label: "Scheduling Error", value: dmOptions.find((option: any) => option.label === "Scheduling Error")?.id },
      ];
    }
    return dmOptions.map((option: any) => ({
      label: option.label,
      value: option.id,
    }));
  }, [dataDMOptions, dataNDMOptions]);

  const handleCancel = useCallback(() => {
    close();
    moveEventData?.revert();
  }, [moveEventData]);

  return (
    <Modal open={blinds} onClose={handleCancel} popupAnimation>
      <ModalContent>
        <BodyDiv>
          <AppText fontSize={16} fontWeight={600} lineHeight={22}>
            Why Did You Move This Event?
          </AppText>

          {formattedDispositionOptions.map((o: any) => (
            <FlexDiv align="center" gap={8} width={204}>
              <PhoenixRadio
                selected={o.value === selectedDisposition}
                onClick={() => {
                  setSelectedDisposition(o.value);
                }}
              />
              <AppText fontSize={12} fontWeight={500} lineHeight={18}>
                {o.label}
              </AppText>
            </FlexDiv>
          ))}
        </BodyDiv>
        <SubmitDiv>
          <PhoenixAppButton
            buttonType="secondary"
            type="button"
            uppercase={true}
            onClick={handleCancel}
            style={{ fontSize: "10px" }}
            height={40}
            variant="danger-outline"
          >
            Cancel
          </PhoenixAppButton>

          <PhoenixAppButton
            buttonType="secondary"
            type="button"
            uppercase={true}
            height={40}
            style={{ fontSize: "10px" }}
            onClick={async () => {
              await editCalendarEvent({
                variables: {
                  id: moveEventData?.id,
                  event_start_time: moment(moveEventData?.start).utc().toISOString(),
                  event_title: moveEventData?.eventTitle,
                  event_description: moveEventData?.eventDescription,
                  disposition_id: selectedDisposition,
                },
              });
            }}
            disabled={loadingDispositionOptions || loadingEdit}
          >
            Continue
          </PhoenixAppButton>
        </SubmitDiv>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  width: 476px;
  height: 326px;
`;

const BodyDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 24px;

  width: 100%;
  padding: 40px;
`;

const SubmitDiv = styled.div`
  position: absolute;
  bottom: 0px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;

  padding: 16px;

  border-top: solid 1px ${theme.NEUTRAL200};
`;

export { MoveEventModal };
